import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { selectAuth } from "features/auth/AuthSlice";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import AccessDenied from "components/common/services/RolesAndPermissions/AccessDenied";
import { t } from "components/CentralDataMangement/translation/Translation";
import CustomNotify from "components/common/atoms/CustomNotify";
import { CANDIDATE_MICROSERVICE } from "Constants";
import Title from "components/common/atoms/Title";
import NavBar from "components/common/atoms/NavBar";
import PaginationNew from "components/common/atoms/PaginationNew";

// Import local components and utilities
import LogFileBrowser from "./LogFileBrowser";
import LogViewerHeader from "./LogViewerHeader";
import LogEntries from "./LogEntries";
import { NAV_ITEMS, INITIAL_FILTERS } from "./constants";
import { isApiAccessDeniedError, callLogApi } from "./apiHelpers";
import {
  ActiveTab,
  LogSource,
  Breadcrumb,
  SelectedFileState,
  Filters,
} from "./Interface";
import DocumentFileIcon from "static/images/DocumentFileIcon";
import DocumentIcon from "static/images/DocumentIcon";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";

const ManageLogs = () => {
  const userAuth = useSelector(selectAuth);

  // Check if user has access to this page
  const allowedEmails = [
  "absolute-jobs-team@infanion.com",
  "shishira.patil@infanion.com",
];

const hasAccess = allowedEmails.includes(userAuth.email);

  const [activeTab, setActiveTab] = useState<ActiveTab>({
    tabFieldName: "microservice",
    tabFieldValue: CANDIDATE_MICROSERVICE,
  });
  const [loadingSources, setLoadingSources] = useState<boolean>(true);
  const [logSources, setLogSources] = useState<LogSource[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [selectedFile, setSelectedFile] = useState<SelectedFileState | null>(
    null
  );
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [viewMode, setViewMode] = useState<"table" | "raw" | "json">("table");
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleApiError = (error: any) => {
    if (isApiAccessDeniedError(error)) {
      setAccessDenied(true);
    } else {
      CustomNotify({
        type: "error",
        message: error.message || t("An unexpected error occurred"),
      });
    }
  };

  const fetchLogSources = useCallback(
    async (path: string = "", targetMicroservice?: string) => {
      setLoadingSources(true);
      setSelectedFile(null); // Clear selection when navigating folders
      setAccessDenied(false);
      try {
        const data = await callLogApi(
          targetMicroservice || activeTab.tabFieldValue,
          userAuth,
          { action: "list", path }
        );
        setLogSources(data.logs || []);
        setBreadcrumbs(data.breadcrumbs || []);
      } catch (error) {
        handleApiError(error);
        setLogSources([]);
        setBreadcrumbs([]);
      } finally {
        setLoadingSources(false);
      }
    },
    [activeTab.tabFieldValue, userAuth]
  );

  const fetchFileContent = useCallback(
    async (filePath: string) => {
      setSelectedFile((prev) => ({ ...prev!, path: filePath, loading: true }));
      try {
        const payload = {
          action: "view",
          path: filePath,
          ...filters,
          page: filters.currentPage,
        };
        const data = await callLogApi(
          activeTab.tabFieldValue,
          userAuth,
          payload
        );
        setSelectedFile({
          name: data.name,
          path: data.path,
          logs: data.logs || [],
          pagination: data.pagination,
          loading: false,
          error: null,
        });
      } catch (error: any) {
        handleApiError(error);
        if (isApiAccessDeniedError(error)) {
          setSelectedFile(null); // Clear file if access is denied
        } else {
          setSelectedFile((prev) =>
            prev ? { ...prev, error: error.message, loading: false } : null
          );
        }
      }
    },
    [activeTab.tabFieldValue, userAuth, filters]
  );

  useEffect(() => {
    if (hasAccess) {
      fetchLogSources("", activeTab.tabFieldValue);
    }
  }, [activeTab.tabFieldValue, fetchLogSources, hasAccess]);

  useEffect(() => {
    if (!selectedFile?.path || !hasAccess) return;
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(
      () => fetchFileContent(selectedFile.path),
      300
    );
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [filters, selectedFile?.path, fetchFileContent, hasAccess]);

  const handleItemClick = (item: LogSource) => {
    if (item.type === "folder") {
      fetchLogSources(item.path);
    } else {
      setFilters(INITIAL_FILTERS); // Reset filters for new file
      setSelectedFile({
        name: item.name,
        path: item.path,
        logs: [],
        pagination: null,
        loading: true,
        error: null,
      });
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string | number) => {
    setFilters((prev) => ({ ...prev, [key]: value, currentPage: 1 }));
  };

  const handlePageChange = (page: number) =>
    setFilters((prev) => ({ ...prev, currentPage: page }));
  const handleItemsPerPageChange = (items: number) =>
    setFilters((prev) => ({
      ...prev,
      recordsPerPage: items,
      currentPage: 1,
    }));

  const handlePrint = () => window.print();

  const handleNavItemClick = (tabFieldName: string, tabFieldValue: string) => {
    setActiveTab({ tabFieldName, tabFieldValue });
  };

  // If user doesn't have access, show access denied
  if (!hasAccess) {
    return <AccessDenied />;
  }

  if (accessDenied) return <AccessDenied />;

  return (
    <AccessControl
      requiredPermissions={[{ permission: "Logs", read: true }]}
      renderNoAccess
    >
      <Title title={t("Log Management System")} />

      {/* Print-specific styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #log-management-page, #log-management-page * { visibility: visible; }
          #log-viewer-card-body, #log-viewer-card-body * { visibility: visible; }
          #log-management-page { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Navigation Bar Section - Matching ManageScraping structure */}
      <div className="search-bar">
        <div className="row">
          <div className="col-md-12 company-tabs">
            <NavBar
              navItems={NAV_ITEMS}
              onNavItemClick={handleNavItemClick}
              className="custom-navbar"
              activeItem={activeTab}
            />
          </div>
        </div>
      </div>

      <div
        id="log-management-page"
        className="position-relative tableMainWrapper"
      >
        <div className="d-flex" style={{ gap: "1vw" }}>
          <div style={{ width: "30%" }}>
            <LogFileBrowser
              sources={logSources}
              loading={loadingSources}
              breadcrumbs={breadcrumbs}
              selectedPath={selectedFile?.path}
              onBreadcrumbClick={fetchLogSources}
              onItemClick={handleItemClick}
            />
          </div>
          <div style={{ width: "70%" }} className="flex-grow-1">
            <div className="card h-100">
              {!selectedFile ? (
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center log-preview">
                    <div className="marginBotttom1">
                      <DocumentIcon />
                    </div>
                    <p>{t("Select a log file to view its content.")}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <LogViewerHeader
                      file={selectedFile}
                      filters={filters}
                      viewMode={viewMode}
                      onFilterChange={handleFilterChange}
                      onRefresh={() => fetchFileContent(selectedFile.path)}
                      setViewMode={setViewMode}
                    />
                  </div>
                  <div id="log-viewer-card-body">
                    {selectedFile.loading ? (
                      <div className="text-center d-flex align-items-center justify-content-center log-entries-content-wrapper">
                        <SpinnerWrapper isDiv={true} />
                      </div>
                    ) : selectedFile.error ? (
                      <div className="p-3">
                        <div className="alert alert-danger m-0">
                          {selectedFile.error}
                        </div>
                      </div>
                    ) : (
                      <LogEntries
                        logs={selectedFile.logs}
                        viewMode={viewMode}
                      />
                    )}
                  </div>
                  {selectedFile.pagination &&
                    !selectedFile.loading &&
                    selectedFile.logs.length > 0 && (
                      <div className="pagination d-block padding1 no-print">
                        <PaginationNew
                          currentPage={filters.currentPage}
                          totalPages={selectedFile.pagination.totalPages}
                          totalRecords={selectedFile.pagination.totalRecords}
                          recordsPerPage={filters.recordsPerPage}
                          onItemsPerPageChange={handleItemsPerPageChange}
                          onPageChange={handlePageChange}
                        />
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AccessControl>
  );
};

export default ManageLogs;
